import { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV_ITEMS = [
  { to: "/", label: "Gallery" },
  { to: "/overview", label: "Overview" },
  { to: "/about", label: "About" },
  { to: "/guestbook", label: "Guestbook" },
  { to: "/inquire", label: "Contact the artist" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [info, setInfo] = useState({ gallery_title: "ART BY John Stam", artist_name: "John Stam" });
  const [visits, setVisits] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false); // close mobile menu on route change
  }, [location.pathname]);

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  // Visit counter: increment once per browser session, otherwise just read.
  useEffect(() => {
    const flag = "gallery_visited_session";
    const alreadyCounted = sessionStorage.getItem(flag);
    const isAdminPath = location.pathname.startsWith("/admin");
    if (!alreadyCounted && !isAdminPath) {
      api
        .post("/visits")
        .then((r) => {
          setVisits(r.data.count);
          sessionStorage.setItem(flag, "1");
        })
        .catch(() => {
          api.get("/visits").then((r) => setVisits(r.data.count)).catch(() => {});
        });
    } else {
      api.get("/visits").then((r) => setVisits(r.data.count)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Spotlight cursor
  useEffect(() => {
    const handler = (e) => {
      document.documentElement.style.setProperty("--mx", `${e.clientX}px`);
      document.documentElement.style.setProperty("--my", `${e.clientY}px`);
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  return (
    <div className="min-h-screen relative wall-vignette grain" data-testid="layout-root">
      <div className="spotlight" aria-hidden="true" />

      {/* Header */}
      <header
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-2xl bg-black/60 border-b border-white/5"
        data-testid="site-header"
      >
        <div className="max-w-[1600px] mx-auto px-5 sm:px-10 py-4 sm:py-5 flex items-center justify-between gap-4">
          <Link to="/" className="group min-w-0" data-testid="logo-link">
            <div className="font-serif-display text-xl sm:text-3xl tracking-tight leading-none truncate">
              {info.gallery_title}
            </div>
            <div className="plaque-label mt-1 hidden sm:block">A Virtual Exhibition</div>
          </Link>
          <nav className="hidden md:flex items-center gap-10">
            {NAV_ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-sm tracking-[0.2em] uppercase font-light transition-colors hover:text-white ${
                    isActive ? "text-white" : "text-zinc-400"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {it.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute -bottom-2 left-0 right-0 h-px bg-[#D4AF37]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 sm:gap-4">
            {user && user.role === "admin" ? (
              <>
                <Link
                  to="/admin"
                  className="text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-white hidden sm:inline"
                  data-testid="header-admin-link"
                >
                  Admin
                </Link>
                <button
                  onClick={logout}
                  className="text-xs tracking-[0.2em] uppercase text-zinc-500 hover:text-white hidden sm:inline"
                  data-testid="header-logout-button"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/admin/login"
                className="text-[6px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white hidden sm:inline"
                data-testid="header-login-link"
              >
                Curator
              </Link>
            )}
            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              className="md:hidden p-2 -mr-2 text-zinc-300 hover:text-white"
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile slide-down nav */}
        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              key="mobile-nav"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="md:hidden overflow-hidden border-t border-white/5 bg-black/85"
              data-testid="mobile-menu"
            >
              <ul className="px-6 py-4 flex flex-col divide-y divide-white/5">
                {NAV_ITEMS.map((it) => (
                  <li key={it.to}>
                    <NavLink
                      to={it.to}
                      end={it.to === "/"}
                      data-testid={`mobile-nav-${it.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `block py-4 text-lg font-serif-display tracking-wide ${
                          isActive ? "text-[#D4AF37]" : "text-zinc-200"
                        }`
                      }
                    >
                      {it.label}
                    </NavLink>
                  </li>
                ))}
                <li>
                  {user && user.role === "admin" ? (
                    <div className="flex items-center justify-between py-4">
                      <Link
                        to="/admin"
                        className="text-xs tracking-[0.3em] uppercase text-zinc-300"
                        data-testid="mobile-nav-admin"
                      >
                        Admin
                      </Link>
                      <button
                        onClick={logout}
                        className="text-xs tracking-[0.3em] uppercase text-zinc-500"
                        data-testid="mobile-nav-logout"
                      >
                        Logout
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/admin/login"
                      className="block py-4 text-xs tracking-[0.3em] uppercase text-zinc-500"
                      data-testid="mobile-nav-login"
                    >
                      Curator
                    </Link>
                  )}
                </li>
              </ul>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <main className="relative z-10 pt-24" data-testid="main-content">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative z-10 mt-32 border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-16 grid md:grid-cols-3 gap-12">
          <div>
            <div className="font-serif-display text-3xl">{info.gallery_title}</div>
            <p className="plaque-label mt-2">Curated Works · MMXXV</p>
          </div>
          <div>
            <p className="plaque-label mb-3">Visit</p>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li>The gallery is open at all hours.</li>
              <li>Admission, complimentary.</li>
            </ul>
          </div>
          <div>
            <p className="plaque-label mb-3">Correspondence</p>
            <Link to="/inquire" className="text-sm text-zinc-300 hover:text-[#D4AF37] block" data-testid="footer-inquire-link">
              Contact the artist →
            </Link>
            <a
              href="https://www.instagram.com/art_by_john_stam/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-[#D4AF37]"
              data-testid="footer-instagram-link"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
              @art_by_john_stam
            </a>
          </div>
        </div>
        <div className="text-center text-xs text-zinc-600 py-6 border-t border-white/5">
          © {new Date().getFullYear()} {info.artist_name}. All works reserved.
          {visits != null && (
            <span className="ml-3" data-testid="visitor-count">
              · {visits.toLocaleString()} {visits === 1 ? "visit" : "visits"}
            </span>
          )}
        </div>
      </footer>
    </div>
  );
}
